import { FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RoutesList } from '../../../../routes/Routes';
import { usePlanningBreachStore } from '../../../../hooks/usePlanningBreachStore';
import { ErrorMessages } from '@types';
import {
  scrollTop,
  dynamicDateErrorMessage,
  parseZodErrors,
  dateAndTimeRangeSchema,
  dateSchema,
  dynamicDateRangeErrorMessage,
} from '@utils';

import { FormTitle, NavigationButtons, TextInputMolecule } from '@molecules/*';
import { ErrorSummary, InlineErrorMessage } from '@atoms/*';
import { DateTemplate } from '../DateTemplate/DateTemplate';

export const DateOfShortTermLetsIssue = () => {
  const navigate = useNavigate();
  const { data, update } = usePlanningBreachStore(); //selectedDate
  const [errorMessages, setErrorMessages] = useState<ErrorMessages[]>([]);
  const [inlineErrorMessage, setInlineErrorMessage] = useState<string>('');
  const title =
    'When did you first notice that the property was being used for short-term lets?';
  const errorPath = 'dateOfIssue';

  const dateDescription = (
    <div className="text-bodyLg text-dark-grey mb-4">
      <p>
        If you're unsure of the exact date, choose the closest one you
        remember.{' '}
      </p>
      <p>For example, 31 01 2024</p>
    </div>
  );

  const resetErrorMessages = () => {
    setErrorMessages([]);
    setInlineErrorMessage('');
  };

  const handleDateInput = (e: FormEvent<HTMLInputElement>) => {
    resetErrorMessages();
    const { name, value } = e.currentTarget;

    const updatedDate = {
      ...data.selectedDate,
      [name]: value,
    };
    update('selectedDate', updatedDate);
  };

  const handleClick = () => {
    resetErrorMessages();
    try {
      dateSchema.parse(data.selectedDate);
      dateAndTimeRangeSchema.parse({
        dayRange: data.selectedDate.selectedDay,
        monthRange: data.selectedDate.selectedMonth,
        yearRange: data.selectedDate.selectedYear,
        minuteRange: 1, //To satisfy time validation
        hourRange: 1, //To satisfy time validation
      });
      navigate(RoutesList.PROPERTY_USAGE);
      return;
    } catch (error: any) {
      const errors = parseZodErrors(error);
      const paths = errors.map((error) => error.path).sort();
      const dynamicInlineErrorMessage = dynamicDateErrorMessage(
        paths,
      ) as string;
      if (dynamicInlineErrorMessage) {
        setInlineErrorMessage(dynamicInlineErrorMessage);
        setErrorMessages(errors);
      } else {
        const newDateRangeErrorMessage = dynamicDateRangeErrorMessage(
          paths,
          errors,
        );
        if (newDateRangeErrorMessage) {
          setInlineErrorMessage(newDateRangeErrorMessage);
          setErrorMessages(errors);
        }
      }
    }
  };

  const buttonPrevious = {
    onClick: () => navigate(RoutesList.ISSUE_LOCATION),
  };

  const buttonNext = {
    onClick: () => {
      handleClick();
      scrollTop();
    },
  };

  return (
    <>
      <div className="flex flex-col gap-y-2">
        <FormTitle text={title} />
        <ErrorSummary messages={errorMessages} />
        <DateTemplate description={dateDescription}>
          <InlineErrorMessage
            path={errorPath}
            messages={[{ path: errorPath, message: inlineErrorMessage }]}
          />
          <div className="flex gap-x-4">
            <section className="w-12">
              <TextInputMolecule
                maxLength={2}
                label="Day"
                name="selectedDay"
                errorPath="daySelected"
                value={data.selectedDate.selectedDay}
                onChange={(e) => handleDateInput(e)}
              />
            </section>
            <section className="w-12">
              <TextInputMolecule
                maxLength={2}
                label="Month"
                name="selectedMonth"
                errorPath="monthSelected"
                value={data.selectedDate.selectedMonth}
                onChange={(e) => handleDateInput(e)}
              />
            </section>
            <section className="w-16">
              <TextInputMolecule
                maxLength={4}
                label="Year"
                name="selectedYear"
                errorPath="yearSelected"
                value={data.selectedDate.selectedYear}
                onChange={(e) => handleDateInput(e)}
              />
            </section>
          </div>
        </DateTemplate>
        <NavigationButtons
          buttonNext={buttonNext}
          buttonPrevious={buttonPrevious}
        />
      </div>
    </>
  );
};
